import SearchIcon from "@/assets/img/symbolic/search.svg?react";
import SearchDecal from "@/assets/img/layout/search/search-decal.svg";
import SearchProfileComponent, {
    ProfileCardSkeleton,
} from "@/components/search/SearchProfile";
import {
    useContext,
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
} from "react";
import { AuthContext } from "@/Realize";
import { LoginButtons } from "@/components/PopUpLogin";
import SearchSection from "@/components/search/SearchSection";
import type { LiveGame, SearchProfile } from "@/local-shared/model";
import social, { SUCCESS } from "@/api/social";
import LiveGameSlot from "@/components/search/LiveGameSlot";
import type { Socket } from "socket.io-client";
import type {
    ClientToServerEvents,
    ServerToClientEvents,
} from "@/local-shared/socket-events";
import Profile from "./Profile/Profile";
import Padding from "@/components/shared/Padding";
import type { TabName } from "./TabData";
import UIListener from "./UIListener";
import { motion, stagger } from "framer-motion";
import { MS_IN_S } from "@/realize-shared/model/Time";
import Spectate from "../Spectate";
import type { RoomID } from "@/realize-shared/model/Types";

const DEBOUNCE_DELAY = 250;

function SearchInput({
    onSubmit,
    onCancel,
}: {
    onSubmit: (query: string) => void;
    onCancel: () => void;
})
{
    const [inputValue, setInputValue] = useState("");

    const [debounceTimeout, setDebounceTimeout] = useState<number | null>(null);

    const [focused, setFocused] = useState(false);

    return (
        <motion.div
            className={
                Wind.style({
                    paddingY: "py-2",
                    paddingX: "px-3",
                    display: "flex",
                    flexDirection: "flex-row",
                    alignItems: "items-center",
                    marginBottom: "mb-6",
                    position: "sticky",
                    top: "top-0",
                    backgroundColor: "bg-[#FECB6B]",
                    zIndex: "z-[25]",
                }).class
            }
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
        >
            <div
                className={
                    Wind.style({
                        borderWidth: "border",
                        borderColor: "border-[#6C3331]/40",
                        backgroundColor: "bg-[#FFD78D]",
                        borderRightWidth: "border-r-0",
                        padding: "p-3",
                        borderLeftRadius: "rounded-l-2xl",
                        height: "h-10",
                        display: "flex",
                        alignItems: "items-center",
                        justifyContent: "justify-center",
                    }).class
                }
            >
                <SearchIcon
                    className={
                        Wind.style({
                            width: "w-6",
                            height: "h-6",
                            color: "text-[#3F1413]",
                        }).class
                    }
                />
            </div>
            <input
                type="text"
                placeholder={focused ? "" : "Search..."}
                className={
                    Wind.style({
                        width: "w-full",
                        padding: "p-3",
                        height: "h-10",
                        paddingLeft: "pl-0",
                        borderWidth: "border",
                        borderColor: "border-[#6C3331]/40",
                        backgroundColor: "bg-[#FFD78D]",
                        color: "text-[#3F1413]",
                        borderRadius: "rounded-2xl",
                        borderLeftRadius: "rounded-l-none",
                        borderLeftWidth: "border-l-0",
                        "::placeholder": {
                            color: "placeholder:text-[#3F1413]",
                            fontWeight: "placeholder:font-bold",
                        },
                        ":focus": {
                            outlineWidth: "focus:outline-0",
                            outlineStyle: "focus:outline-none",
                        },
                    }).class
                }
                onFocus={() => void setFocused(true)}
                onBlur={() => void setFocused(false)}
                onChange={(e) => void setInputValue(e.target.value)}
                value={inputValue}
                onKeyUp={() =>
                {
                    // Debounce search: call onSubmit after user stops typing for 500ms
                    if (debounceTimeout)
                    {
                        clearTimeout(debounceTimeout);
                    }

                    setDebounceTimeout(
                        setTimeout(() =>
                        {
                            onSubmit(inputValue);
                        }, DEBOUNCE_DELAY)
                    );
                }}
            />
            <button
                className={
                    Wind.style({
                        paddingX: "px-3",
                        color: "text-[#3F1413]",
                    }).class
                }
                onClick={() =>
                {
                    if (debounceTimeout)
                    {
                        clearTimeout(debounceTimeout);
                    }

                    if (inputValue.trim() === "")
                    {
                        onCancel();
                    }

                    onSubmit("");
                    setInputValue("");
                }}
            >
                Cancel
            </button>
        </motion.div>
    );
}

export interface SearchRef
{
    reloadProfileHeadlines: () => void;
}

const CHILD_STAGGER = 0.5;

const Search = forwardRef<
    SearchRef,
    {
        socket: Socket<ServerToClientEvents, ClientToServerEvents>;
        setActiveTab: (tab: TabName) => void;
        setHeadlineSubmissionID: (
            data: {
                id: number;
                headline: string;
            } | null
        ) => void;
    }
>(({ socket, setActiveTab, setHeadlineSubmissionID }, ref) =>
            {
                const userData = useContext(AuthContext);

                const [recentProfiles, setRecentProfiles] = useState<SearchProfile[]>([]);
                const [isSearchResults, setIsSearchResults] = useState(false);
                const [searchProfiles, setSearchProfiles] = useState<SearchProfile[]>([]);
                const [isLoadingRecent, setIsLoadingRecent] = useState(true);

                const [selectedProfile, setSelectedProfile] = useState<string | null>(null);

                const [liveGames, setLiveGames] = useState<LiveGame[]>([]);

                const [spectateGame, setSpectateGame] = useState<string | null>(null);

                const [refreshTrigger, setRefreshTrigger] = useState(0);

                useImperativeHandle(ref, () => ({
                    reloadProfileHeadlines: () =>
                    {
                        // Trigger a refresh by updating the refresh trigger
                        setRefreshTrigger((prev) => prev + 1);
                    },
                }));

                const fetchRecentProfiles = async () =>
                {
                    try
                    {
                        const results = await social.getRecentProfiles();

                        if (results)
                        {
                            if (results.status === SUCCESS)
                            {
                                setRecentProfiles(results.data ?? []);
                            }
                            else
                            {
                                Logger.error(
                                    "Failed to fetch recent profiles:",
                                    results.message
                                );

                                setRecentProfiles([]);
                            }

                            setIsLoadingRecent(false);
                        }
                    }
                    catch (error)
                    {
                        Logger.error("Error fetching recent profiles:", error);
                        setIsLoadingRecent(false);
                    }
                };

                const fetchLiveGames = async () =>
                {
                    try
                    {
                        const results = await social.search.liveGames();

                        if (results.status === SUCCESS)
                        {
                            setLiveGames(results.data ?? []);
                        }
                        else
                        {
                            Logger.error("Failed to fetch live games:", results.message);
                            setLiveGames([]);
                        }
                    }
                    catch (error)
                    {
                        Logger.error("Error fetching live games:", error);
                        setLiveGames([]);
                    }
                };

                const handleSearch = async (query: string) =>
                {
                    if (query.trim() === "")
                    {
                        Logger.warn("Search query is empty.");
                        setIsSearchResults(false);

                        return;
                    }

                    setIsLoadingRecent(true);

                    try
                    {
                        const results = await social.searchRealize(query);

                        Logger.log("Search results:", results);

                        if (typeof results === "object")
                        {
                            setSearchProfiles(results.data ?? []);
                            setIsSearchResults(true);
                        }
                    }
                    catch (error)
                    {
                        Logger.error("Error in handleSearch:", error);
                        setIsSearchResults(false);
                    }
                    finally
                    {
                        setIsLoadingRecent(false);
                    }
                };

                useEffect(() =>
                {
                    if (localStorage.getItem("activeTab"))
                    {
                        setTimeout(() =>
                        {
                            localStorage.removeItem("activeTab");
                        }, MS_IN_S / 2);
                    }

                    if (userData)
                    {
                        fetchRecentProfiles().catch(
                            (error) =>
                                void Logger.error(
                                    "Error in useEffect fetchRecentProfiles:",
                                    error
                                )
                        );

                        fetchLiveGames().catch(
                            (error) =>
                                void Logger.error(
                                    "Error in useEffect fetchLiveGames:",
                                    error
                                )
                        );
                    }
                    else
                    {
                        setRecentProfiles([]);
                        setIsLoadingRecent(false);
                    }
                }, [userData]);

                const reducedLiveGames = liveGames.filter((game) =>
                {
                    return game.players.length > 0 && game.timeLeft - Date.now() > 0;
                });

                return (
                    <>
                        <div
                            className={
                                Wind.style({
                                    height: "h-full",
                                    display: "flex",
                                    flexDirection: "flex-col",
                                    flex: "flex-[1_1_100%]",
                                }).class
                            }
                        >
                            {userData && (
                                <SearchInput
                                    onSubmit={(value) =>
                                    {
                                        handleSearch(value).catch((error) =>
                                        {
                                            Logger.error("Error in handleSearch:", error);
                                        });
                                    }}
                                    onCancel={() =>
                                    {
                                        setActiveTab("home");
                                    }}
                                />
                            )}
                            {userData && isSearchResults && (
                                <>
                                    {/* <SearchSection
                        title="Live Games"
                        className={Wind.style({
                            flexGrow: "grow",

                        }).class}
                    >
                        test
                    </SearchSection> */}
                                    <SearchSection
                                        title="Search Results"
                                        className={
                                            Wind.style({
                                                flexGrow: "grow",
                                                paddingBottom: "pb-[--safe-area-keyboard]",
                                            }).class
                                        }
                                    >
                                        {searchProfiles.length === 0 && (
                                            <div
                                                className={
                                                    Wind.style({
                                                        flex: "flex-1",
                                                        display: "flex",
                                                        flexDirection: "flex-col",
                                                        alignItems: "items-center",
                                                        justifyContent: "justify-center",
                                                    }).class
                                                }
                                            >
                                                <img
                                                    src={SearchDecal}
                                                    alt="Search Decal"
                                                    className={
                                                        Wind.style({
                                                            width: "w-full",
                                                            height: "h-auto",
                                                            marginX: "mx-auto",
                                                            maxWidth: "max-w-[210px]",
                                                        }).class
                                                    }
                                                />
                                                <h1
                                                    className={
                                                        Wind.style({
                                                            fontFamily: "font-poppins",
                                                            fontSize: "text-xl",
                                                            fontWeight: "font-bold",
                                                            lineHeight: "leading-6",
                                                            color: "text-[#3F1413]",
                                                            marginTop: "mt-4",
                                                            marginBottom: "mb-1",
                                                            letterSpacing: "tracking-[0%]",
                                                        }).class
                                                    }
                                                >
                                                    No Results Found
                                                </h1>
                                                <p
                                                    className={
                                                        Wind.style({
                                                            fontFamily: "font-poppins",
                                                            fontSize: "text-base",
                                                            fontWeight: "font-normal",
                                                            lineHeight: "leading-6",
                                                            color: "text-[#3F1413]/70",
                                                            marginBottom: "mb-4",
                                                            textAlign: "text-center",
                                                        }).class
                                                    }
                                                >
                                                    No Results Were Found
                                                </p>
                                            </div>
                                        )}
                                        {searchProfiles.map((account, accountIndex) => (
                                            <SearchProfileComponent
                                                key={account.id}
                                                index={accountIndex}
                                                account={account}
                                                onProfileClicked={(profileId) =>
                                                {
                                                    Logger.log("Embed Profile Over Search");

                                                    setSelectedProfile(
                                                        profileId.toString()
                                                    );
                                                }}
                                            />
                                        ))}
                                    </SearchSection>
                                </>
                            )}
                            {userData && !isSearchResults && (
                                <>
                                    {reducedLiveGames.length > 0 && (
                                        <SearchSection
                                            title="Live Games"
                                            className={
                                                Wind.style({
                                                    display: "grid",
                                                    gridTemplateColumns:
                                            "grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]",
                                                    gap: "gap-2",
                                                    marginBottom: "mb-4",
                                                }).class
                                            }
                                        >
                                            {reducedLiveGames.map((game, gameIndex) =>
                                            {
                                                return (
                                                    <LiveGameSlot
                                                        key={game.id}
                                                        gameData={game}
                                                        socket={socket}
                                                        gameIndex={gameIndex}
                                                        spectateGame={setSpectateGame}
                                                    />
                                                );
                                            })}
                                        </SearchSection>
                                    )}
                                    <SearchSection title="Recently Joined Photographers">
                                        {isLoadingRecent && (
                                            <>
                                                <ProfileCardSkeleton />
                                                <ProfileCardSkeleton />
                                                <ProfileCardSkeleton />
                                                <ProfileCardSkeleton />
                                            </>
                                        )}
                                        <motion.div
                                            transition={{
                                                duration: 3,
                                                delayChildren: stagger(CHILD_STAGGER, {
                                                    startDelay: CHILD_STAGGER,
                                                    ease: "easeOut",
                                                }),
                                            }}
                                        >
                                            {recentProfiles.map((account, accountIndex) => (
                                                <SearchProfileComponent
                                                    key={account.id}
                                                    account={account}
                                                    index={accountIndex}
                                                    onProfileClicked={(profileId) =>
                                                    {
                                                        Logger.log(
                                                            "Embed Profile Over Search"
                                                        );

                                                        setSelectedProfile(
                                                            profileId.toString()
                                                        );
                                                    }}
                                                />
                                            ))}
                                        </motion.div>
                                    </SearchSection>
                                </>
                            )}
                            {!isSearchResults && (
                                <div
                                    className={
                                        Wind.style({
                                            flex: "flex-1",
                                            display: "flex",
                                            flexDirection: "flex-col",
                                            alignItems: "items-center",
                                            justifyContent: "justify-center",
                                        }).class
                                    }
                                >
                                    <img
                                        src={SearchDecal}
                                        alt="Search Decal"
                                        className={
                                            Wind.style({
                                                width: "w-full",
                                                height: "h-auto",
                                                marginX: "mx-auto",
                                                maxWidth: "max-w-[210px]",
                                            }).class
                                        }
                                    />
                                    <h1
                                        className={
                                            Wind.style({
                                                fontFamily: "font-poppins",
                                                fontSize: "text-xl",
                                                fontWeight: "font-bold",
                                                lineHeight: "leading-6",
                                                color: "text-[#3F1413]",
                                                marginTop: "mt-4",
                                                marginBottom: "mb-1",
                                                letterSpacing: "tracking-[0%]",
                                            }).class
                                        }
                                    >
                                        Find Realizations
                                    </h1>
                                    <p
                                        className={
                                            Wind.style({
                                                fontFamily: "font-poppins",
                                                fontSize: "text-base",
                                                fontWeight: "font-normal",
                                                lineHeight: "leading-6",
                                                color: "text-[#3F1413]/70",
                                                marginBottom: "mb-4",
                                                textAlign: "text-center",
                                            }).class
                                        }
                                    >
                                        {userData && "see what others are realizing"}
                                        {!userData
                                && "sign in to see what others are realizing"}
                                    </p>
                                    {!userData && (
                                        <div
                                            className={
                                                Wind.style({
                                                    width: "w-full",
                                                    paddingX: "px-4",
                                                }).class
                                            }
                                        >
                                            <LoginButtons />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {selectedProfile && (
                            <div
                                className={
                                    Wind.style({
                                        position: "fixed",
                                        top: "top-0",
                                        left: "left-0",
                                        right: "right-0",
                                        zIndex: "z-50",
                                        display: "flex",
                                        flexDirection: "flex-col",
                                    }).class
                                }
                                style={{
                                    background:
                            "linear-gradient(rgb(246, 237, 226) 0%, rgb(255, 229, 197) 100%)",
                                    bottom: "calc(var(--safe-area-bottom,0px) + 4.5rem)",
                                }}
                            >
                                <Padding
                                    top
                                    wrapper={
                                        Wind.style({
                                            display: "flex",
                                            flexDirection: "flex-col",
                                            flex: "flex-1",
                                            width: "w-full",
                                        }).class
                                    }
                                    className={
                                        Wind.style({
                                            display: "flex",
                                            flexDirection: "flex-col",
                                        }).class
                                    }
                                >
                                    <Profile
                                        profileID={selectedProfile}
                                        setActiveTab={setActiveTab}
                                        refreshTrigger={refreshTrigger}
                                        setHeadlineSubmissionID={(id) =>
                                        {
                                            // Noop
                                            Logger.log("Show Headline Submission ID", id);
                                            setHeadlineSubmissionID(id);
                                        }}
                                        onBackPress={() =>
                                        {
                                            setSelectedProfile(null);

                                            UIListener.trigger("updateSelectedProfile", "");
                                        }}
                                    />
                                </Padding>
                            </div>
                        )}
                        {spectateGame && (
                            <div
                                className={
                                    Wind.style({
                                        position: "fixed",
                                        top: "top-0",
                                        left: "left-0",
                                        right: "right-0",
                                        bottom: "bottom-0",
                                        zIndex: "z-50",
                                        display: "flex",
                                        flexDirection: "flex-col",
                                    }).class
                                }
                                style={{
                                    background:
                            "linear-gradient(rgb(246, 237, 226) 0%, rgb(255, 229, 197) 100%)",
                                }}
                            >
                                <Padding
                                    wrapper={
                                        Wind.style({
                                            display: "flex",
                                            flexDirection: "flex-col",
                                            flex: "flex-1",
                                            width: "w-full",
                                        }).class
                                    }
                                    className={
                                        Wind.style({
                                            display: "flex",
                                            flexDirection: "flex-col",
                                        }).class
                                    }
                                >
                                    <Spectate
                                        roomID={spectateGame as RoomID}
                                        socket={socket}
                                        exit={() =>
                                        {
                                            Logger.log("Resetting Spectate");
                                            setSpectateGame(null);
                                        }}
                                    />
                                </Padding>
                            </div>
                        )}
                    </>
                );
            });

Search.displayName = "Search";

export default Search;
